import * as React from "react";
import { Box, Container, Typography } from "@mui/material";

/* ─── Brand data: 3 rows, ~29 each ──────────────────────── */
const row1 = [
  "Aircel","Airtel","AXE","B21","BalleBaazi","Beardo","Bingo!",
  "Brand Factory","Burger King","Cadbury","dhani","Dharma Productions",
  "dineout","Domino's Pizza","Dream11","Eros Now","Esselworld","ET Money",
  "Fantasy Gaming","Flipkart","20th Century Fox","Free Fire","goibibo",
  "Good Knight","gainon","mad4music","McVitie's","Mother Dairy","MPL",
];

const row2 = [
  "Mountain Dew","Myntra","Netflix","Niine","OnePlus","OPPO",
  "Parachute Advansed","Paytm First Games","Paytm","Pepsi","Prime Video",
  "Reliance Fresh","RSVP","Sa Re Ga Ma Pa","SBI","Set Wet","ShareChat",
  "Sony Liv","Star Sports","Smule","Storytel","Sunfeast","T-Series","Tide",
  "TikTok","Tinder U","trell","U-Dictionary","Universal",
];

const row3 = [
  "upGrad","UPI","Upstox","Vedantu","Vigo Video","Vodafone","Voot",
  "VYRL Originals","Wakefit","Wild Stone","WinZo","WorkIndia","ZEE5",
  "Zee Bollywood","Zee Music Co.","Zee Punjabi","Zee TV","Zee Vajwa",
  "happn","Helo","hotstar","Imperial Blue","INOX","Jelimals",
  "Kent RO","Khatabook","Likee","Bulbul",
];

/* ─── Stat pills beneath marquee ────────────────────────── */
const stats = [
  { v: "1000+",   l: "Brands Served"           },
  { v: "3000+",   l: "Campaigns Executed"       },
  { v: "₹50Cr+",  l: "Creator Payouts Processed" },
];

/* ─── Single brand pill ──────────────────────────────────── */
function BrandPill({ name }) {
  return (
    <Box
      sx={{
        display:       "inline-flex",
        alignItems:    "center",
        gap:           "8px",
        mx:            "9px",
        px:            "20px",
        py:            "10px",
        border:        "1px solid rgba(255,255,255,0.09)",
        borderRadius:  "100px",
        bgcolor:       "rgba(255,255,255,0.025)",
        whiteSpace:    "nowrap",
        flexShrink:    0,
        transition:    "all 0.22s ease",
        cursor:        "default",
        "&:hover": {
          bgcolor:     "rgba(255,107,53,0.09)",
          borderColor: "rgba(255,107,53,0.35)",
          transform:   "scale(1.05)",
        },
      }}
    >
      <Box
        sx={{
          width:        5,
          height:       5,
          borderRadius: "50%",
          bgcolor:      "rgba(255,107,53,0.65)",
          flexShrink:   0,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontSize:      "13px",
          fontWeight:    600,
          color:         "rgba(255,255,255,0.68)",
          letterSpacing: "0.015em",
          userSelect:    "none",
        }}
      >
        {name}
      </Typography>
    </Box>
  );
}

/* ─── One infinite marquee row ───────────────────────────── */
function MarqueeRow({ brands, direction = "left", speed = 50 }) {
  const doubled = [...brands, ...brands];
  const animName = direction === "right" ? "marquee-right" : "marquee-left";
  return (
    <Box
      className="brand-row"
      sx={{ overflow: "hidden", width: "100%", mb: "14px", "&:last-child": { mb: 0 } }}
    >
      <Box
        className="brand-track"
        sx={{
          display:   "flex",
          width:     "max-content",
          animation: `${animName} ${speed}s linear infinite`,
        }}
      >
        {doubled.map((name, i) => (
          <BrandPill key={i} name={name} />
        ))}
      </Box>
    </Box>
  );
}

/* ─── Section ────────────────────────────────────────────── */
function LandingTestimonials() {
  return (
    <Box
      id="brands_container"
      sx={{
        bgcolor:  "#0D1B3E",
        position: "relative",
        overflow: "hidden",
        py:       { xs: "60px", md: "90px" },
      }}
    >
      {/* ── Ambient glows (no canvas — marquee provides motion) ── */}
      <Box sx={{
        position:     "absolute",
        width:        700, height: 700,
        top:          "-180px", left: "8%",
        background:   "radial-gradient(circle, rgba(255,107,53,0.055) 0%, transparent 65%)",
        pointerEvents: "none",
      }} />
      <Box sx={{
        position:     "absolute",
        width:        600, height: 600,
        bottom:       "-120px", right: "6%",
        background:   "radial-gradient(circle, rgba(74,144,226,0.06) 0%, transparent 65%)",
        pointerEvents: "none",
      }} />

      {/* ── Header ── */}
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1, mb: { xs: "40px", md: "56px" } }}>
        <Box className="reveal" sx={{ textAlign: "center" }}>

          {/* Badge */}
          <Box
            component="span"
            sx={{
              display:       "inline-block",
              color:         "#FF6B35",
              bgcolor:       "rgba(255,107,53,0.10)",
              border:        "1px solid rgba(255,107,53,0.28)",
              px:            "14px", py: "5px",
              borderRadius:  "100px",
              fontSize:      "10px", fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.08em",
              mb:            2.5,
            }}
          >
            Brand Partners
          </Box>

          {/* Heading */}
          <Typography
            component="h2"
            sx={{
              fontSize:   "clamp(26px, 3.2vw, 40px)",
              fontWeight: 800,
              color:      "white",
              lineHeight: 1.2,
              mb:         1.5,
            }}
          >
            Trusted by{" "}
            <Box
              component="span"
              sx={{
                background:           "linear-gradient(90deg, #FF6B35 0%, #FF9A6C 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor:  "transparent",
                backgroundClip:       "text",
              }}
            >
              India's top brands
            </Box>
          </Typography>

          {/* Sub */}
          <Typography
            sx={{
              fontSize:  "15px",
              color:     "rgba(255,255,255,0.40)",
              maxWidth:  "520px",
              mx:        "auto",
              lineHeight: 1.70,
            }}
          >
            From D2C startups to Fortune 500 giants — Meesha powers influencer
            campaigns at every scale, across every industry.
          </Typography>
        </Box>
      </Container>

      {/* ── Marquee rows (full-width, outside Container) ── */}
      <Box sx={{ position: "relative", zIndex: 1 }}>

        {/* Left gradient fade */}
        <Box sx={{
          position:     "absolute",
          left:         0, top: 0, bottom: 0,
          width:        { xs: "72px", md: "160px" },
          background:   "linear-gradient(90deg, #0D1B3E 0%, transparent 100%)",
          zIndex:       2,
          pointerEvents: "none",
        }} />
        {/* Right gradient fade */}
        <Box sx={{
          position:     "absolute",
          right:        0, top: 0, bottom: 0,
          width:        { xs: "72px", md: "160px" },
          background:   "linear-gradient(270deg, #0D1B3E 0%, transparent 100%)",
          zIndex:       2,
          pointerEvents: "none",
        }} />

        <MarqueeRow brands={row1} direction="left"  speed={48} />
        <MarqueeRow brands={row2} direction="right" speed={44} />
        <MarqueeRow brands={row3} direction="left"  speed={52} />
      </Box>

      {/* ── Bottom stats ── */}
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1, mt: { xs: "44px", md: "60px" } }}>
        <Box
          className="reveal"
          sx={{
            display:       "flex",
            flexWrap:      "wrap",
            justifyContent: "center",
            gap:            { xs: "28px", md: "64px" },
          }}
        >
          {stats.map(({ v, l }) => (
            <Box key={l} sx={{ textAlign: "center" }}>

              {/* Divider line above stat */}
              <Box sx={{
                width:   "32px",
                height:  "2px",
                bgcolor: "rgba(255,107,53,0.40)",
                mx:      "auto",
                mb:      1.5,
                borderRadius: "2px",
              }} />

              <Typography
                sx={{
                  fontSize:   "clamp(24px, 2.8vw, 34px)",
                  fontWeight: 800,
                  color:      "white",
                  lineHeight: 1,
                }}
              >
                {v}
              </Typography>
              <Typography
                sx={{
                  fontSize:      "11px",
                  color:         "rgba(255,255,255,0.36)",
                  textTransform: "uppercase",
                  letterSpacing: "0.09em",
                  mt:            0.75,
                  fontWeight:    600,
                }}
              >
                {l}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Thin bottom separator */}
        <Box sx={{
          mt:         { xs: "36px", md: "52px" },
          height:     "1px",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)",
        }} />
      </Container>
    </Box>
  );
}

export default LandingTestimonials;
